import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { CreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBar";
import { getCreditDisplayProps } from "@src/appV2/redesign/EmergencyCredits/utils/getCreditDisplayProps";
import {
  type CreditProgressBarLabelStatus,
  getCreditProgressBarLabel,
} from "@src/appV2/redesign/EmergencyCredits/utils/getCreditProgressBarLabel";
import {
  GOLDEN_RATIO_BOTTOM_FLEX,
  GOLDEN_RATIO_TOP_FLEX,
} from "@src/appV2/redesign/utils/goldenRatioLayout";
import { useCallback, useState } from "react";

import { type YourEmergencyCreditsPageProps } from "./types";

function shouldShowTooltip(
  completedShifts: number,
  requiredShifts: number,
  hasCredit: boolean
): boolean {
  // Show tooltip if: less than full progress AND doesn't have a credit already
  const hasPartialProgress = completedShifts < requiredShifts;
  return hasPartialProgress && !hasCredit;
}

export function YourEmergencyCreditsPage(props: YourEmergencyCreditsPageProps) {
  const {
    backgroundGradient,
    cancellationStats,
    latenessStats,
    gainedCancellationCredit,
    gainedLatenessCredit,
    usedLatenessCredit,
    onContinue,
    onLearnMoreClick,
  } = props;

  const gainedAnyCredit = gainedCancellationCredit || gainedLatenessCredit;

  const hasCancellationCredit = cancellationStats.totalAvailableCredits > 0;
  const hasLatenessCredit = latenessStats.totalAvailableCredits > 0 || usedLatenessCredit;

  // A bar "will animate" when it runs in fill mode with visible progress.
  // This is broader than just gained*Credit — it also covers the "no credit
  // yet but some shifts completed" case where the bar fills from 0.
  const cancellationWillAnimate =
    gainedCancellationCredit ||
    (!hasCancellationCredit && cancellationStats.completedShiftsTowardsNextCredit > 0);
  const latenessWillAnimate =
    gainedLatenessCredit ||
    (!hasLatenessCredit && latenessStats.completedShiftsTowardsNextCredit > 0);

  const [isCancellationAnimComplete, setIsCancellationAnimComplete] =
    useState(!cancellationWillAnimate);
  const [isLatenessAnimComplete, setIsLatenessAnimComplete] = useState(!latenessWillAnimate);

  const handleCancellationAnimComplete = useCallback(() => {
    setIsCancellationAnimComplete(true);
  }, []);

  const handleLatenessAnimComplete = useCallback(() => {
    setIsLatenessAnimComplete(true);
  }, []);

  const isAnimationComplete = isCancellationAnimComplete && isLatenessAnimComplete;

  function getCreditLabelStatus({
    hasExistingCredit,
    gainedCredit,
  }: {
    hasExistingCredit: boolean;
    gainedCredit: boolean;
  }): CreditProgressBarLabelStatus {
    if (gainedCredit) {
      return isAnimationComplete ? "earned" : "unavailable";
    }

    return hasExistingCredit ? "available" : "unavailable";
  }

  const cancellationDisplay = getCreditDisplayProps(cancellationStats, {
    gainedCredit: gainedCancellationCredit,
  });
  const latenessDisplay = getCreditDisplayProps(latenessStats, {
    gainedCredit: gainedLatenessCredit,
    hasCredit: hasLatenessCredit,
  });

  return (
    <PageWrapper
      sx={(theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        background: backgroundGradient ?? theme.palette.background.tertiary,
        display: "flex",
        flexDirection: "column",
      })}
    >
      <Stack sx={{ flex: 1 }}>
        <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingX: 4,
            paddingTop: 2,
          }}
        >
          {/* Header Section */}
          <Stack spacing={4} alignItems="center" textAlign="center" sx={{ width: "100%" }}>
            <Text variant="h3">
              {gainedAnyCredit ? "You've earned an emergency credit!" : "Your Emergency Credits"}
            </Text>

            {!gainedAnyCredit && (
              <Text
                variant="h6"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                })}
              >
                Working shifts earns progress towards credits that protect your Clipboard Score.
              </Text>
            )}
          </Stack>

          {/* Progress Card Section */}
          <Box
            sx={(theme) => ({
              marginTop: 12,
              backgroundColor: theme.palette.background.primary,
              borderRadius: "15px",
              paddingX: 6,
              paddingTop: 6,
              paddingBottom: 8,
              width: "100%",
            })}
          >
            <Stack spacing={5}>
              {/* Cancellation Credit Progress */}
              <CreditProgressBar
                completedCount={cancellationDisplay.completedCount}
                requiredCount={cancellationDisplay.requiredCount}
                label={getCreditProgressBarLabel(
                  "cancellation",
                  getCreditLabelStatus({
                    hasExistingCredit: hasCancellationCredit,
                    gainedCredit: gainedCancellationCredit,
                  })
                )}
                creditType="cancellation"
                mode={hasCancellationCredit && !gainedCancellationCredit ? "static" : "fill"}
                showTooltip={
                  !gainedCancellationCredit &&
                  shouldShowTooltip(
                    cancellationStats.completedShiftsTowardsNextCredit,
                    cancellationStats.requiredShiftsForNextCredit,
                    hasCancellationCredit
                  )
                }
                popIconOnFull={gainedCancellationCredit}
                onAnimationComplete={handleCancellationAnimComplete}
              />

              {/* Lateness Credit Progress */}
              <CreditProgressBar
                completedCount={latenessDisplay.completedCount}
                requiredCount={latenessDisplay.requiredCount}
                label={getCreditProgressBarLabel(
                  "lateness",
                  getCreditLabelStatus({
                    hasExistingCredit: hasLatenessCredit,
                    gainedCredit: gainedLatenessCredit,
                  })
                )}
                creditType="lateness"
                mode={hasLatenessCredit && !gainedLatenessCredit ? "static" : "fill"}
                showTooltip={
                  !gainedLatenessCredit &&
                  shouldShowTooltip(
                    latenessStats.completedShiftsTowardsNextCredit,
                    latenessStats.requiredShiftsForNextCredit,
                    hasLatenessCredit
                  )
                }
                popIconOnFull={gainedLatenessCredit}
                onAnimationComplete={handleLatenessAnimComplete}
              />
            </Stack>
          </Box>
        </Box>

        <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />
      </Stack>

      <DialogFooter orientation="horizontal">
        <Stack spacing={5} alignItems="center" sx={{ width: "100%" }}>
          <TextButton onClick={onLearnMoreClick}>What are Emergency Credits?</TextButton>

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isAnimationComplete}
            onClick={onContinue}
          >
            Continue
          </Button>
        </Stack>
      </DialogFooter>
    </PageWrapper>
  );
}
